/**
 * ThemeToggle.jsx - Granullo Glass System v2
 * 
 * Sun/Moon icon toggle in TopNav with dropdown for Dark/Light/System modes.
 * 
 * Design:
 * - Compact trigger button showing current mode icon
 * - Dropdown with 3 options: System, Light, Dark
 * - Checkmark indicator for active selection
 * - One-time hint on first visit (dismissable)
 */

import React, { useEffect, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Sun, Moon, Monitor, ChevronDown, Check } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { toast } from 'sonner';

const HINT_KEY = 'granullo_theme_hint_dismissed';

const themeOptions = [
  { value: 'system', label: 'System', icon: Monitor, description: 'Follow OS preference' },
  { value: 'light', label: 'Light', icon: Sun, description: 'Apple-calm brightness' },
  { value: 'dark', label: 'Dark', icon: Moon, description: 'Institutional intelligence' },
];

export function ThemeToggle({ showLabel = true, compact = false }) {
  const { theme, resolvedTheme, setTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [hasShownHint, setHasShownHint] = useState(true);

  // Theme hint removed — no demo signals

  const getCurrentIcon = () => {
    if (theme === 'system') {
      return Monitor;
    }
    return resolvedTheme === 'dark' ? Moon : Sun;
  };

  const getCurrentLabel = () => {
    if (theme === 'system') return 'System';
    return theme === 'dark' ? 'Dark' : 'Light';
  };

  const CurrentIcon = getCurrentIcon();

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm"
          className={`
            h-8 gap-1.5 
            text-[var(--g-text-secondary)] hover:text-[var(--g-text)] 
            hover:bg-[var(--g-nav-item-bg-hover)]
            ${compact ? 'px-2' : 'px-2.5'}
          `}
          data-testid="theme-toggle"
          title={`Theme: ${getCurrentLabel()} (click to change)`}
          aria-label={`Current theme: ${getCurrentLabel()}. Click to change theme.`}
        >
          <CurrentIcon className="h-4 w-4" />
          {showLabel && !compact && (
            <span className="text-[12px] hidden sm:inline">{getCurrentLabel()}</span>
          )}
          <ChevronDown className={`h-3 w-3 opacity-50 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent 
        align="end" 
        className="w-48 bg-[var(--g-surface-solid)] border border-[var(--g-border)] shadow-[var(--g-shadow-mid)] rounded-[var(--g-radius-lg)]"
        data-testid="theme-dropdown"
      >
        {themeOptions.map((option) => {
          const Icon = option.icon;
          const isActive = theme === option.value;
          
          return (
            <DropdownMenuItem 
              key={option.value}
              onClick={() => {
                setTheme(option.value);
                setIsOpen(false);
              }}
              className={`
                flex items-center gap-3 cursor-pointer py-2.5 px-3
                rounded-[var(--g-radius-md)] mx-1 my-0.5
                ${isActive 
                  ? 'bg-[var(--g-action-blue-08)] text-[var(--g-text)]' 
                  : 'text-[var(--g-text-secondary)] hover:text-[var(--g-text)] hover:bg-[var(--g-surface)]'
                }
              `}
              data-testid={`theme-${option.value}`}
            >
              <Icon className={`h-4 w-4 ${isActive ? 'text-[var(--g-action-blue)]' : ''}`} />
              <div className="flex-1">
                <span className="text-[13px] font-medium">{option.label}</span>
                <p className="text-[11px] opacity-70">{option.description}</p>
              </div>
              {isActive && (
                <Check className="h-4 w-4 text-[var(--g-action-blue)]" />
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * ThemeToggleCompact - Icon-only version for tight spaces
 */
export function ThemeToggleCompact() {
  return <ThemeToggle showLabel={false} compact />;
}

export default ThemeToggle;
